import React, { useEffect, useMemo, useState } from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { ChevronLeft, ChevronRight, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import {
  UnifiedTable,
  UnifiedTableColumn,
} from '@/components/ui/unifiedTable/UnifiedTable';

const PAGE_SIZE = 100;
const MAX_PAGE_SIZE = 1000;

const allPageSize = (totalCount: number) => Math.min(Math.max(totalCount, PAGE_SIZE), MAX_PAGE_SIZE);

const DRUG_BANK_MEDICATIONS = gql`
  query DrugBankMedications(
    $search: String
    $active: Boolean
    $psychotropic: Boolean
    $page: Int
    $pageSize: Int
    $sortField: String
    $sortDirection: String
  ) {
    drugBankMedications(
      search: $search
      active: $active
      psychotropic: $psychotropic
      page: $page
      pageSize: $pageSize
      sortField: $sortField
      sortDirection: $sortDirection
    ) {
      totalCount
      rows {
        uniqueid
        name
        description
        dosages
        createdBy
        updatedBy
        createdAt
        updatedAt
        active
        psychotropic
      }
    }
  }
`;

const IMPORT_DRUG_BANK_MEDICATIONS = gql`
  mutation ImportDrugBankMedicationsFromMedcall {
    importDrugBankMedicationsFromMedcall {
      totalCount
      insertedCount
      skippedCount
      message
    }
  }
`;

const CREATE_DRUG_BANK_MEDICATION = gql`
  mutation CreateDrugBankMedication($input: DrugBankMedicationInput!) {
    createDrugBankMedication(input: $input) {
      message
      medication {
        uniqueid
      }
    }
  }
`;

const UPDATE_DRUG_BANK_MEDICATION = gql`
  mutation UpdateDrugBankMedication($uniqueid: Int!, $input: DrugBankMedicationInput!) {
    updateDrugBankMedication(uniqueid: $uniqueid, input: $input) {
      message
      medication {
        uniqueid
      }
    }
  }
`;

const DELETE_DRUG_BANK_MEDICATION = gql`
  mutation DeleteDrugBankMedication($uniqueid: Int!) {
    deleteDrugBankMedication(uniqueid: $uniqueid) {
      message
      medication {
        uniqueid
      }
    }
  }
`;

type DrugBankMedication = {
  uniqueid: number;
  name: string;
  description?: string | null;
  dosages?: string[] | null;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  active: boolean;
  psychotropic: boolean;
};

type MedicationFormState = {
  uniqueid: number | null;
  name: string;
  description: string;
  dosages: string[];
  active: boolean;
  psychotropic: boolean;
};

type PageSizeOption = '100' | '200' | 'all';

const emptyForm = (): MedicationFormState => ({
  uniqueid: null,
  name: '',
  description: '',
  dosages: [],
  active: true,
  psychotropic: false,
});

const cleanDosages = (values: string[]) => {
  const seen = new Set<string>();
  const dosages: string[] = [];

  values.forEach((value) => {
    const normalized = `${value || ''}`.trim();
    const key = normalized.toLowerCase();
    if (!normalized || seen.has(key)) {
      return;
    }
    seen.add(key);
    dosages.push(normalized);
  });

  return dosages;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const dosageSummary = (dosages?: string[] | null) => {
  const normalized = cleanDosages(Array.isArray(dosages) ? dosages : []);
  if (normalized.length < 1) {
    return <span className="text-[var(--neutral-gray-80)]">None</span>;
  }

  const visible = normalized.slice(0, 4);
  const hiddenCount = normalized.length - visible.length;

  return (
    <div className="flex max-w-[420px] flex-wrap gap-1">
      {visible.map((dosage) => (
        <Badge key={dosage} variant="secondary" className="rounded-sm">
          {dosage}
        </Badge>
      ))}
      {hiddenCount > 0 && (
        <Badge variant="outline" className="rounded-sm">
          +{hiddenCount}
        </Badge>
      )}
    </div>
  );
};

const booleanBadge = (enabled: boolean, enabledLabel: string, disabledLabel: string) => (
  <Badge variant={enabled ? 'default' : 'secondary'} className="rounded-sm">
    {enabled ? enabledLabel : disabledLabel}
  </Badge>
);

export const DrugBankMedicationManager: React.FC = () => {
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'disabled'>('active');
  const [psychotropicFilter, setPsychotropicFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [pageSizeOption, setPageSizeOption] = useState<PageSizeOption>('100');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState<'ASC' | 'DESC'>('ASC');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState<MedicationFormState>(() => emptyForm());
  const [dosageDraft, setDosageDraft] = useState('');

  const queryVariables = useMemo(() => ({
    search: search || undefined,
    active: activeFilter === 'all' ? undefined : activeFilter === 'active',
    psychotropic: psychotropicFilter === 'all' ? undefined : psychotropicFilter === 'yes',
    page,
    pageSize,
    sortField,
    sortDirection,
  }), [activeFilter, page, pageSize, psychotropicFilter, search, sortDirection, sortField]);

  const { data, previousData, loading, error, refetch } = useQuery(DRUG_BANK_MEDICATIONS, {
    variables: queryVariables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const [importMedications, { loading: importing }] = useMutation(IMPORT_DRUG_BANK_MEDICATIONS);
  const [createMedication, { loading: creating }] = useMutation(CREATE_DRUG_BANK_MEDICATION);
  const [updateMedication, { loading: updating }] = useMutation(UPDATE_DRUG_BANK_MEDICATION);
  const [deleteMedication, { loading: deleting }] = useMutation(DELETE_DRUG_BANK_MEDICATION);

  const medicationList = data?.drugBankMedications || previousData?.drugBankMedications;
  const hasCurrentMedicationList = Boolean(data?.drugBankMedications);
  const rows: DrugBankMedication[] = medicationList?.rows || [];
  const totalCount = Number(medicationList?.totalCount || 0);
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const saving = creating || updating;
  const dialogBusy = saving || deleting;
  const firstVisibleRow = totalCount < 1 ? 0 : Math.min((page - 1) * pageSize + 1, totalCount);
  const lastVisibleRow = totalCount < 1 ? 0 : Math.min((page - 1) * pageSize + rows.length, totalCount);

  useEffect(() => {
    if (!hasCurrentMedicationList) {
      return;
    }
    if (page <= totalPages) {
      return;
    }
    setPage(totalPages);
  }, [hasCurrentMedicationList, page, totalPages]);

  useEffect(() => {
    if (pageSizeOption !== 'all') {
      return;
    }

    const nextPageSize = allPageSize(totalCount);
    setPageSize((current) => (current === nextPageSize ? current : nextPageSize));
  }, [pageSizeOption, totalCount]);

  const openCreateDialog = () => {
    setForm(emptyForm());
    setDosageDraft('');
    setDialogOpen(true);
  };

  const openEditDialog = (row: DrugBankMedication) => {
    setForm({
      uniqueid: row.uniqueid,
      name: row.name || '',
      description: row.description || '',
      dosages: cleanDosages(row.dosages || []),
      active: row.active !== false,
      psychotropic: row.psychotropic === true,
    });
    setDosageDraft('');
    setDialogOpen(true);
  };

  const submitSearch = (value?: string) => {
    setSearch(`${value ?? searchDraft}`.trim());
    setPage(1);
  };

  const handleSortChange = (key: string, direction: 'asc' | 'desc') => {
    setSortField(key);
    setSortDirection(direction === 'desc' ? 'DESC' : 'ASC');
    setPage(1);
  };

  const handlePageSizeChange = (value: PageSizeOption) => {
    setPage(1);
    setPageSizeOption(value);
    setPageSize(value === 'all' ? allPageSize(totalCount) : Number(value));
  };

  const handleImport = async () => {
    try {
      const result = await importMedications();
      const summary = result.data?.importDrugBankMedicationsFromMedcall;
      toast.success(summary?.message || 'MedCall medications imported.');
      setPage(1);
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Unable to import MedCall medications.');
    }
  };

  const addDosage = () => {
    const nextValue = dosageDraft.trim();
    if (!nextValue) {
      return;
    }
    setForm((current) => ({
      ...current,
      dosages: cleanDosages([...current.dosages, nextValue]),
    }));
    setDosageDraft('');
  };

  const updateDosage = (index: number, value: string) => {
    setForm((current) => {
      const nextDosages = [...current.dosages];
      nextDosages[index] = value;
      return { ...current, dosages: nextDosages };
    });
  };

  const removeDosage = (index: number) => {
    setForm((current) => ({
      ...current,
      dosages: current.dosages.filter((_value, currentIndex) => currentIndex !== index),
    }));
  };

  const handleSave = async () => {
    const name = form.name.trim();
    if (!name) {
      toast.error('Medication name is required.');
      return;
    }

    const input = {
      name,
      description: form.description.trim(),
      dosages: cleanDosages(form.dosages),
      active: form.active,
      psychotropic: form.psychotropic,
    };

    try {
      if (form.uniqueid) {
        await updateMedication({
          variables: {
            uniqueid: form.uniqueid,
            input,
          },
        });
        toast.success('Medication updated.');
      } else {
        await createMedication({ variables: { input } });
        toast.success('Medication created.');
      }

      setDialogOpen(false);
      setForm(emptyForm());
      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Unable to save medication.');
    }
  };

  const handleDelete = async () => {
    const uniqueid = form.uniqueid;
    if (!uniqueid || dialogBusy) {
      return;
    }

    const medicationName = form.name.trim() || 'this medication';
    if (!window.confirm(`Delete ${medicationName}? This cannot be undone.`)) {
      return;
    }

    try {
      await deleteMedication({ variables: { uniqueid } });
      toast.success('Medication deleted.');
      setDialogOpen(false);
      setForm(emptyForm());
      setDosageDraft('');

      const nextPage = rows.length <= 1 && page > 1 ? page - 1 : page;
      if (nextPage !== page) {
        setPage(nextPage);
        return;
      }

      await refetch();
    } catch (mutationError: any) {
      toast.error(mutationError?.message || 'Unable to delete medication.');
    }
  };

  const renderPaginationControls = () => (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <span className="text-xs text-[var(--neutral-gray-90)]">
        Showing {firstVisibleRow.toLocaleString()}-{lastVisibleRow.toLocaleString()} of {totalCount.toLocaleString()}
      </span>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page <= 1}
          onClick={() => setPage((current) => Math.max(1, current - 1))}
          aria-label="Previous page"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="min-w-[80px] text-center text-xs text-[var(--neutral-gray-90)]">
          {Math.min(page, totalPages)} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 w-8 p-0"
          disabled={page >= totalPages}
          onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          aria-label="Next page"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
      <select
        value={pageSizeOption}
        onChange={(event) => handlePageSizeChange(event.target.value as PageSizeOption)}
        className="h-8 w-[90px] rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white px-2 text-xs"
        aria-label="Items per page"
      >
        <option value="100">100/page</option>
        <option value="200">200/page</option>
        <option value="all">{totalCount > MAX_PAGE_SIZE ? `${MAX_PAGE_SIZE}/page` : 'All'}</option>
      </select>
    </div>
  );

  const columns = useMemo<UnifiedTableColumn<DrugBankMedication>[]>(() => [
    {
      key: 'name',
      header: 'Medication',
      sortable: true,
      width: '260px',
      accessor: (row) => (
        <div className="min-w-[220px]">
          <div className="font-medium text-[var(--neutral-gray-120)]">{row.name}</div>
          {row.description ? (
            <div className="mt-0.5 max-w-[420px] truncate text-[11px] text-[var(--neutral-gray-90)]">
              {row.description}
            </div>
          ) : null}
        </div>
      ),
      searchValue: (row) => `${row.name || ''} ${row.description || ''}`,
    },
    {
      key: 'dosages',
      header: 'Dosages',
      accessor: (row) => dosageSummary(row.dosages),
      searchValue: (row) => (row.dosages || []).join(' '),
    },
    {
      key: 'active',
      header: 'Status',
      sortable: true,
      accessor: (row) => booleanBadge(row.active !== false, 'Active', 'Disabled'),
      align: 'center',
      dataType: 'boolean',
    },
    {
      key: 'psychotropic',
      header: 'Psychotropic',
      sortable: true,
      accessor: (row) => (
        <div className="flex justify-center">
          <Checkbox checked={row.psychotropic === true} disabled aria-label="Psychotropic" />
        </div>
      ),
      align: 'center',
      dataType: 'boolean',
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      sortable: true,
      accessor: (row) => (
        <div className="min-w-[150px]">
          <div>{formatDateTime(row.updatedAt)}</div>
          {row.updatedBy ? (
            <div className="text-[11px] text-[var(--neutral-gray-80)]">{row.updatedBy}</div>
          ) : null}
        </div>
      ),
      dataType: 'datetime',
    },
  ], []);

  return (
    <div className="flex min-h-full flex-col bg-[var(--surface-page)] p-4 text-[var(--neutral-gray-120)]">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-normal">DrugBank Medication Manager</h1>
          <div className="mt-1 text-sm text-[var(--neutral-gray-90)]">
            {totalCount.toLocaleString()} medication{totalCount === 1 ? '' : 's'}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={importing}
            onClick={handleImport}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${importing ? 'animate-spin' : ''}`} />
            Import MedCall
          </Button>
          <Button type="button" size="sm" onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" />
            New Medication
          </Button>
        </div>
      </div>

      {error ? (
        <div className="mb-3 rounded-[var(--border-radius-medium)] border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error.message}
        </div>
      ) : null}

      <UnifiedTable
        columns={columns}
        data={rows}
        rowKey={(row) => String(row.uniqueid)}
        searchEnabled
        clientSearch={false}
        searchValue={searchDraft}
        onSearchValueChange={setSearchDraft}
        onSearchSubmit={submitSearch}
        searchPlaceholder="Search medications"
        searchButtonLabel="Search"
        loading={loading}
        emptyTitle="No medications found"
        emptyDescription="No rows match the current filters."
        sortKey={sortField}
        sortDirection={sortDirection === 'DESC' ? 'desc' : 'asc'}
        clientSort={false}
        onSortChange={handleSortChange}
        toolbarLeft={
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={activeFilter}
              onChange={(event) => {
                setActiveFilter(event.target.value as typeof activeFilter);
                setPage(1);
              }}
              className="h-8 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white px-2 text-xs"
              aria-label="Status filter"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            <select
              value={psychotropicFilter}
              onChange={(event) => {
                setPsychotropicFilter(event.target.value as typeof psychotropicFilter);
                setPage(1);
              }}
              className="h-8 rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-50)] bg-white px-2 text-xs"
              aria-label="Psychotropic filter"
            >
              <option value="all">All Classes</option>
              <option value="yes">Psychotropic</option>
              <option value="no">Not Psychotropic</option>
            </select>
          </div>
        }
        toolbarRight={renderPaginationControls()}
        showRowActions
        stickyRowActions
        rowActionsHeader=""
        renderRowActions={(row) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            onClick={() => openEditDialog(row)}
            aria-label={`Edit ${row.name}`}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
        )}
      />

      <div className="mt-2 flex justify-end border-t border-[var(--neutral-gray-30)] pt-2">
        {renderPaginationControls()}
      </div>

      <div className="h-16 shrink-0" aria-hidden="true" />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent contentWidth="760px" contentMaxWidth="96vw" contentHeight="auto" contentMaxHeight="90vh">
          <DialogHeader>
            <DialogTitle>{form.uniqueid ? 'Edit Medication' : 'New Medication'}</DialogTitle>
          </DialogHeader>

          <div className="grid gap-4 overflow-auto pr-1">
            <div className="grid gap-1.5">
              <Label htmlFor="drugbank-medication-name">Medication</Label>
              <Input
                id="drugbank-medication-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
              />
            </div>

            <div className="grid gap-1.5">
              <Label htmlFor="drugbank-medication-description">Description</Label>
              <Textarea
                id="drugbank-medication-description"
                rows={4}
                value={form.description}
                onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              />
            </div>

            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex items-center justify-between rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] px-3 py-2">
                <Label htmlFor="drugbank-medication-active">Active</Label>
                <Switch
                  id="drugbank-medication-active"
                  checked={form.active}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, active: checked === true }))}
                />
              </div>

              <div className="flex items-center justify-between rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-40)] px-3 py-2">
                <Label htmlFor="drugbank-medication-psychotropic">Psychotropic</Label>
                <Checkbox
                  id="drugbank-medication-psychotropic"
                  checked={form.psychotropic}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, psychotropic: checked === true }))}
                />
              </div>
            </div>

            <div className="grid gap-2">
              <Label>Dosages</Label>
              <div className="grid gap-2">
                {form.dosages.map((dosage, index) => (
                  <div key={`${index}-${dosage}`} className="flex items-center gap-2">
                    <Input
                      value={dosage}
                      onChange={(event) => updateDosage(index, event.target.value)}
                      onBlur={() => setForm((current) => ({ ...current, dosages: cleanDosages(current.dosages) }))}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="h-9 w-9 shrink-0 p-0"
                      onClick={() => removeDosage(index)}
                      aria-label={`Remove ${dosage}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2">
                <Input
                  value={dosageDraft}
                  onChange={(event) => setDosageDraft(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      addDosage();
                    }
                  }}
                  placeholder="10mg"
                />
                <Button type="button" variant="outline" onClick={addDosage}>
                  <Plus className="mr-2 h-4 w-4" />
                  Add
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter className="items-center justify-between sm:justify-between">
            <div className="flex w-full justify-start sm:w-auto">
              {form.uniqueid ? (
                <Button type="button" variant="destructive" onClick={handleDelete} disabled={dialogBusy}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              ) : null}
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button type="button" onClick={handleSave} disabled={dialogBusy}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={dialogBusy}>
                Cancel
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DrugBankMedicationManager;
